import React, { useEffect, useRef, useState } from 'react';
import { JitsiMeeting } from '@jitsi/react-sdk';
import { callAPI } from '../helper/apiUtils';
import { $user } from '../helper/UserFactory';
import appConfig from '../config/appConfig';
import { unmarkCallActive } from '../utils/callTab';
import "./CallWindow.css";

// Standalone Jitsi call page rendered in its OWN browser tab (see index.js,
// which mounts this instead of <App/> when the path is "/call"). Keeping it out
// of the main app tree means the original Kjuup tab stays fully usable while a
// call is in progress. This component intentionally does NOT depend on Redux
// call state — it is self-contained and talks to the backend directly using the
// jwt token from localStorage (shared across same-origin tabs).
const CallWindow = () => {
    const params = new URLSearchParams(window.location.search);
    const roomId = params.get('room') || '';
    const roomName = params.get('name') || '';
    const isGroupChat = params.get('group') === '1';
    // Audio call (video=0) starts with the camera muted; video call (video=1)
    // starts with the camera on. The user can toggle it manually either way.
    const startWithVideoMuted = params.get('video') !== '1';

    const user = $user.get();
    const token = $user.getToken();

    const [showLoader, setShowLoader] = useState(true);
    const hasLeftRef = useRef(false);

    // Best-effort leave that survives tab close. `videoConferenceLeft` (hangup)
    // uses the normal awaited call; the unload path uses keepalive fetch because
    // the browser may kill the tab before a normal request finishes. sendBeacon
    // can't set the Authorization header the BE requires, so we use keepalive.
    const leaveCall = (useKeepalive) => {
        if (hasLeftRef.current) return;
        hasLeftRef.current = true;
        unmarkCallActive(roomId);
        if (!roomId) return;
        if (useKeepalive) {
            try {
                fetch(`${appConfig.API_URL}/videochat/leave`, {
                    method: 'POST',
                    keepalive: true,
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        Authorization: `jwt ${token}`,
                    },
                    body: JSON.stringify({ room: roomId }),
                });
            } catch (_) { /* best effort */ }
        } else {
            callAPI('/videochat/leave', {}, 'POST', { room: roomId });
        }
    };

    useEffect(() => {
        const onUnload = () => leaveCall(true);
        window.addEventListener('pagehide', onUnload);
        window.addEventListener('beforeunload', onUnload);
        return () => {
            window.removeEventListener('pagehide', onUnload);
            window.removeEventListener('beforeunload', onUnload);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleApiReady = (api) => {
        setShowLoader(false);
        api.addListener('videoConferenceLeft', () => {
            // Use the keepalive path: we close the tab immediately after, and a
            // normal request could be aborted by the tab unloading before it
            // flushes. keepalive lets the leave complete regardless.
            leaveCall(true);
            try { window.close(); } catch (_) { /* ignore */ }
        });
    };

    if (!token || !user) {
        return (
            <div className="call-window__message">
                Bitte melde dich in Kjuup an, um dem Anruf beizutreten.
            </div>
        );
    }

    if (!roomId) {
        return (
            <div className="call-window__message">
                Kein Anruf gefunden.
            </div>
        );
    }

    const displayName = `${user.firstName || ''} ${user.lastName || ''}`.trim();

    return (
        <div className="call-window">
            {showLoader && (
                <div className="call-window__loader">
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            )}
            <JitsiMeeting
                domain="jitsi.kjuup.de"
                roomName={roomId}
                configOverwrite={{
                    startWithAudioMuted: true,
                    startWithVideoMuted: startWithVideoMuted,
                    startScreenSharing: false,
                    enableEmailInStats: false,
                    'prejoinConfig.enabled': false,
                    subject: isGroupChat ? (roomName || ' ') : ' ',
                    // Disable P2P so 2-participant (DM) calls behave exactly like
                    // group calls — all media flows through the JVB bridge instead
                    // of a direct peer connection. This keeps DM calls stable and
                    // consistent with multi-party calls (no separate 1:1 path).
                    p2p: { enabled: false },
                }}
                interfaceConfigOverwrite={{
                    APP_NAME: 'Kjuup',
                    SHOW_JITSI_WATERMARK: false,
                    TOOLBAR_BUTTONS: [
                        'microphone',
                        'camera',
                        'closedcaptions',
                        'desktop',
                        'embedmeeting',
                        'fullscreen',
                        'hangup',
                        'chat',
                        'recording',
                        'livestreaming',
                        'etherpad',
                        'sharedvideo',
                        'settings',
                        'raisehand',
                        'videoquality',
                        'filmstrip',
                        'invite',
                        'stats',
                        'shortcuts',
                        'tileview',
                        'download',
                        'help',
                        'mute-everyone',
                        'security',
                    ],
                }}
                userInfo={{
                    displayName: displayName,
                    email: user.email,
                }}
                onApiReady={handleApiReady}
                getIFrameRef={(iframeRef) => {
                    iframeRef.style.height = 'var(--app-height)';
                }}
            />
        </div>
    );
};

export default CallWindow;
